import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { SectionHeader, StatTile, Pill } from "@/components/Brand";
import {
  MapPin,
  Building2,
  Users,
  Plus,
  Trash2,
  Pencil,
  Search,
  X,
} from "lucide-react";
import { toast } from "sonner";

export default function AdminWards() {
  const [wards, setWards] = useState([]);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null); // ward or null
  const [boothModal, setBoothModal] = useState(null); // ward id
  const [overview, setOverview] = useState(null);

  const load = async () => {
    const [w, o] = await Promise.all([
      api.get("/admin/wards"),
      api.get("/admin/analytics/overview"),
    ]);
    setWards(w.data);
    setOverview(o.data);
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = wards.filter(
    (w) =>
      w.name.toLowerCase().includes(search.toLowerCase()) ||
      w.district.toLowerCase().includes(search.toLowerCase()) ||
      w.number.toLowerCase().includes(search.toLowerCase()),
  );

  const removeWard = async (id) => {
    if (!window.confirm("Delete this ward and all its booths/surveys?")) return;
    try {
      await api.delete(`/admin/wards/${id}`);
      toast.success("Ward deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div>
      <SectionHeader
        eyebrow="Geography"
        title="Wards & Booths"
        action={
          <button
            onClick={() => setEditing({})}
            data-testid="new-ward-btn"
            className="inline-flex items-center gap-1.5 bg-blue-700 hover:bg-blue-800 text-white px-4 py-2 rounded-md text-sm font-medium"
          >
            <Plus size={16} /> New Ward
          </button>
        }
      />

      {overview && (
        <div className="wi-stat-row mb-6">
          <StatTile label="Total Wards" value={overview.total_wards} icon={MapPin} />
          <StatTile label="Total Booths" value={overview.total_booths} icon={Building2} />
          <StatTile label="Active Surveyors" value={overview.active_surveyors} icon={Users} />
          <StatTile label="Avg Coverage" value={`${overview.avg_coverage}/booth`} />
        </div>
      )}

      <div className="flex items-center gap-2 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            placeholder="Search by ward, district or number…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="ward-search-input"
            className="w-full pl-9 pr-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent"
          />
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map((w) => (
          <div key={w.id} className="wi-card p-5" data-testid={`ward-card-${w.name.toLowerCase()}`}>
            <div className="flex items-start justify-between">
              <div>
                <Pill tone="blue">{w.district}</Pill>
                <div className="mt-2 text-lg font-semibold text-slate-900">{w.name}</div>
                <div className="data-label mt-0.5">Ward #{w.number}</div>
              </div>
              <div className="text-[10px] font-mono text-slate-400">
                ID-{w.id.slice(0, 6)}
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2 mt-4">
              <div>
                <div className="data-label">Booths</div>
                <div className="metric-value text-lg">{w.booth_count}</div>
              </div>
              <div>
                <div className="data-label">Surveys</div>
                <div className="metric-value text-lg">{w.survey_count}</div>
              </div>
              <div>
                <div className="data-label">Per Booth</div>
                <div className="metric-value text-lg">
                  {w.booth_count ? Math.round(w.survey_count / w.booth_count) : 0}
                </div>
              </div>
            </div>
            <div className="mt-4 flex gap-2">
              <button
                onClick={() => setBoothModal(w)}
                data-testid={`manage-booths-${w.name.toLowerCase()}`}
                className="flex-1 inline-flex items-center justify-center gap-1.5 text-sm bg-slate-900 hover:bg-slate-800 text-white px-3 py-2 rounded-md"
              >
                Manage Booths
              </button>
              <button
                onClick={() => setEditing(w)}
                data-testid={`edit-ward-${w.name.toLowerCase()}`}
                className="p-2 border border-slate-200 rounded-md hover:bg-slate-50"
                aria-label="Edit ward"
              >
                <Pencil size={14} />
              </button>
              <button
                onClick={() => removeWard(w.id)}
                data-testid={`delete-ward-${w.name.toLowerCase()}`}
                className="p-2 border border-slate-200 rounded-md hover:bg-red-50 hover:text-red-600"
                aria-label="Delete ward"
              >
                <Trash2 size={14} />
              </button>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <WardModal
          ward={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            load();
          }}
        />
      )}
      {boothModal && (
        <BoothModal
          ward={boothModal}
          onClose={() => setBoothModal(null)}
          onChanged={load}
        />
      )}
    </div>
  );
}

function WardModal({ ward, onClose, onSaved }) {
  const editing = !!ward.id;
  const [form, setForm] = useState({
    name: ward.name || "",
    number: ward.number || "",
    district: ward.district || "",
    description: ward.description || "",
  });
  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing) await api.put(`/admin/wards/${ward.id}`, form);
      else await api.post(`/admin/wards`, form);
      toast.success(editing ? "Ward updated" : "Ward created");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };
  return (
    <Modal title={editing ? "Edit Ward" : "New Ward"} onClose={onClose}>
      <form onSubmit={save} className="space-y-4">
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Ward name *">
            <input required data-testid="ward-form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="wi-input" />
          </Field>
          <Field label="Ward number *">
            <input required data-testid="ward-form-number" value={form.number} onChange={(e) => setForm({ ...form, number: e.target.value })} className="wi-input" />
          </Field>
          <Field label="District *">
            <input required data-testid="ward-form-district" value={form.district} onChange={(e) => setForm({ ...form, district: e.target.value })} className="wi-input" />
          </Field>
        </div>
        <Field label="Description">
          <textarea data-testid="ward-form-desc" rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="wi-input" />
        </Field>
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-3 py-1.5 text-sm rounded-md border border-slate-200">Cancel</button>
          <button type="submit" data-testid="ward-form-save" className="px-4 py-1.5 text-sm rounded-md bg-blue-700 hover:bg-blue-800 text-white">Save</button>
        </div>
      </form>
    </Modal>
  );
}

function BoothModal({ ward, onClose, onChanged }) {
  const [booths, setBooths] = useState([]);
  const [form, setForm] = useState({ booth_number: "", polling_station: "", area: "" });
  const [editingBooth, setEditingBooth] = useState(null);

  const load = async () => {
    const r = await api.get(`/wards/${ward.id}/booths`);
    setBooths(r.data);
  };
  useEffect(() => {
    load();
  }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editingBooth) {
        await api.put(`/admin/booths/${editingBooth.id}`, form);
      } else {
        await api.post(`/admin/wards/${ward.id}/booths`, form);
      }
      toast.success("Saved");
      setForm({ booth_number: "", polling_station: "", area: "" });
      setEditingBooth(null);
      load();
      onChanged();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete booth and its surveys?")) return;
    await api.delete(`/admin/booths/${id}`);
    load();
    onChanged();
  };

  return (
    <Modal title={`${ward.name} — Booths`} onClose={onClose} wide>
      <div className="grid md:grid-cols-2 gap-6">
        <form onSubmit={save} className="space-y-3 p-4 bg-slate-50 rounded-lg">
          <h4 className="font-semibold text-slate-900 text-sm">
            {editingBooth ? "Edit booth" : "Add new booth"}
          </h4>
          <Field label="Booth number *">
            <input required data-testid="booth-form-number" value={form.booth_number} onChange={(e) => setForm({ ...form, booth_number: e.target.value })} className="wi-input" />
          </Field>
          <Field label="Polling station *">
            <input required data-testid="booth-form-station" value={form.polling_station} onChange={(e) => setForm({ ...form, polling_station: e.target.value })} className="wi-input" />
          </Field>
          <Field label="Area / Street *">
            <input required data-testid="booth-form-area" value={form.area} onChange={(e) => setForm({ ...form, area: e.target.value })} className="wi-input" />
          </Field>
          <div className="flex gap-2">
            <button type="submit" data-testid="booth-form-save" className="px-3 py-1.5 text-sm rounded-md bg-blue-700 hover:bg-blue-800 text-white">
              {editingBooth ? "Update" : "Add booth"}
            </button>
            {editingBooth && (
              <button type="button" onClick={() => { setEditingBooth(null); setForm({ booth_number: "", polling_station: "", area: "" }); }} className="px-3 py-1.5 text-sm rounded-md border border-slate-200">Cancel</button>
            )}
          </div>
        </form>
        <div className="max-h-[400px] overflow-y-auto pr-1">
          {booths.length === 0 && <div className="text-sm text-slate-500">No booths yet.</div>}
          <ul className="space-y-2">
            {booths.map((b) => (
              <li key={b.id} className="border border-slate-200 rounded-md p-3 flex items-start justify-between gap-2">
                <div className="text-sm">
                  <div className="font-medium text-slate-900">{b.booth_number}</div>
                  <div className="text-slate-600">{b.polling_station}</div>
                  <div className="text-xs text-slate-500">{b.area}</div>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => { setEditingBooth(b); setForm({ booth_number: b.booth_number, polling_station: b.polling_station, area: b.area }); }} className="p-1.5 hover:bg-slate-100 rounded">
                    <Pencil size={12} />
                  </button>
                  <button onClick={() => remove(b.id)} className="p-1.5 hover:bg-red-50 hover:text-red-600 rounded">
                    <Trash2 size={12} />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
      <style>{`.wi-input{ width:100%; border:1px solid #cbd5e1; border-radius:6px; padding:0.5rem 0.65rem; font-size:0.85rem; } .wi-input:focus{ outline:2px solid #1d4ed8; outline-offset:2px; border-color:transparent; }`}</style>
    </Modal>
  );
}

function Modal({ title, onClose, children, wide }) {
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-center justify-center p-4 overflow-y-auto">
      <div className={`bg-white rounded-lg w-full ${wide ? "max-w-3xl" : "max-w-lg"} p-6 shadow-xl`}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">{title}</h3>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded"><X size={16} /></button>
        </div>
        {children}
      </div>
      <style>{`.wi-input{ width:100%; border:1px solid #cbd5e1; border-radius:6px; padding:0.5rem 0.65rem; font-size:0.85rem; } .wi-input:focus{ outline:2px solid #1d4ed8; outline-offset:2px; border-color:transparent; }`}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-slate-700 mb-1">{label}</span>
      {children}
    </label>
  );
}
